import {
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGCollaborationStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { CheckCircle2, Circle, UserCheck } from "lucide-react";
import { observer } from "mobx-react-lite";

const DataEntryDisclosureSidebar = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    currentDisclosureCode,
    setCurrentDisclosure,
    isDisclosureCompleted,
  } = useESGDataEntryStore();

  const { selectedCodesByCategory, groupedByCategory } =
    useESGDisclosureSelectionStore();

  const { isDisclosureDelegated, getDisclosureDelegation } =
    useESGCollaborationStore();

  const selectedCodes = selectedCodesByCategory[activeCategory] || [];

  const selectedDisclosures = selectedCodes
    .map((code) =>
      groupedByCategory[activeCategory]?.find((d) => d.code === code),
    )
    .filter(Boolean);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-radius bg-card border-border border">
      <div className="p-3 border-b border-border">
        <p className="text-sm font-semibold">{t("Selected Disclosures")}</p>
      </div>
      <div className="flex-1 min-h-0 space-y-1 p-3 overflow-y-auto">
        {selectedDisclosures.map((disclosure) => {
          const { code, title } = disclosure;
          const isActive = currentDisclosureCode === code;
          const isCompleted = isDisclosureCompleted(code);
          const delegated = isDisclosureDelegated(code);
          const delegation = delegated ? getDisclosureDelegation(code) : null;

          return (
            <div
              role="button"
              className={`p-1 rounded-md flex flex-col gap-y-0.5 text-sm ${isActive ? "bg-primary/10" : "hover:bg-muted"} ${delegated ? "opacity-60" : ""}`}
              key={code}
              onClick={() => setCurrentDisclosure(code)}
            >
              <div className="flex gap-x-1 items-center">
                {isCompleted ? (
                  <CheckCircle2 size={14} className="text-green-500" />
                ) : (
                  <Circle size={14} className="text-muted-foreground" />
                )}
                <p className="flex-1 text-xs truncate">
                  {code} : {title}
                </p>
              </div>
              {disclosure.isMasterMetricGroup && (
                <div className="flex flex-wrap gap-x-1 gap-y-0.5 ml-5">
                  {disclosure.frameworks?.map((fw) => (
                    <span
                      key={fw}
                      className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold"
                    >
                      {fw}
                    </span>
                  ))}
                </div>
              )}
              {delegated && (
                <div className="flex items-center gap-x-1 ml-5">
                  <UserCheck
                    size={10}
                    className="text-amber-600 dark:text-amber-400"
                  />
                  <p className="text-xs text-amber-700 dark:text-amber-400 truncate">
                    {delegation?.email}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default DataEntryDisclosureSidebar;
